import { useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  AssociationEntity,
  CustomFieldEntityEnum,
  IOfferingSummaryResSchema,
  IProductResSchema,
  IUsageType,
  PrePaidCreditConfigTypeEnum,
  ProductStatusEnum,
  ProductTypeEnum,
  ProrationPolicyEnum,
  UsageTypeStatusEnum,
} from '@monetize/types/app';
import { DEFAULT_PAGER } from '@monetize/types/common';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdInfo, MdLock, MdOutlineConstruction } from 'react-icons/md';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  apiGetAllList,
  apiPatch,
  handleApiErrorToast,
} from '../../../api/axios';
import { doGetProductById } from '../../../api/productCatalogService';
import {
  useCreateEntity,
  useGetById,
  useGetListData,
  useUpdateEntity,
} from '../../../api/queryUtils';
import { useRevRecSettings } from '../../../api/revenueService';
import { CustomFieldDataForm } from '../../../components/CustomFields/CustomFieldDataForm';
import { ExternalAssociationsIcons } from '../../../components/ExternalAssociations/ExternalAssociationsIcons';
import {
  MBox,
  MButton,
  MCheckbox,
  MCustomNumberInput,
  MDivider,
  MFlex,
  MFormField,
  MGrid,
  MGridItem,
  MIcon,
  MInput,
  MLockedTextOrContent,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MSwitch,
  MText,
  MTooltip,
  RadioStyleButtonGroup,
} from '../../../components/Monetize';
import { MCustomMultiSelect } from '../../../components/Monetize/MCustomSelect/MCustomMultiSelect';
import { MCustomSelect } from '../../../components/Monetize/MCustomSelect/MCustomSelect';
import { useCustomSelectValues } from '../../../components/Monetize/MCustomSelect/useCustomSelectValue';
import { PRODUCTS, ROUTES } from '../../../constants';
import {
  CUSTOM_ID_PLACEHOLDER,
  SKU_PLACEHOLDER,
  STATUS_PLACEHOLDER,
} from '../../../constants/placeholders';
import {
  DISPLAY_FREQUENCY_ALLOWED_PRODUCT_TYPE,
  FREQUENCY_ITEMS,
} from '../../../constants/quotes';
import { PRODUCT_LIST } from '../../../constants/routes';
import { useDebounce } from '../../../hooks/useDebounce';
import { useCtrlEnterHotkey } from '../../../hooks/useHotkeys';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import { useFlags } from '../../../services/launchDarkly';
import { logFormError } from '../../../services/logger';
import { getHeaderTitle } from '../../../utils/productCatalog';
import { BucketForm } from '../Buckets/BucketForm';
import { BucketSearch } from '../Offerings/components/BucketSearch';
import { UsageTypeForm } from '../UsageTypes/UsageTypeForm';
import { UsageTypeView } from '../UsageTypes/UsageTypeView';
import ExtraConfigurationTitle from './ExtraConfigurationTitle';
import { ProductActions } from './ProductActions';
import {
  buildProductPatchPayload,
  getProductFormValues,
  ProductFormSchema,
  ProductFormValues,
} from './productForm.utils';
import { RelatedOfferingsSection } from './RelatedOfferingsSection';

const displayFrequencyOptions = FREQUENCY_ITEMS.map(({ title, value }) => ({
  label: title,
  value: `${value}`,
}));

const renderPrePaidCreditScopeItemContent = ({
  title,
  item,
  isSubtitle,
  isSelected,
  isHighlight,
}: {
  title: string;
  item: any;
  isSubtitle: boolean;
  isSelected: boolean;
  isHighlight: boolean;
}) => {
  if (
    [
      PrePaidCreditConfigTypeEnum.ALL_PRODUCTS,
      PrePaidCreditConfigTypeEnum.SUBSCRIPTION_PRODUCTS_ONLY,
      PrePaidCreditConfigTypeEnum.SPECIFIC_PRODUCTS,
    ].includes(item.value)
  ) {
    return (
      <MTooltip label="Coming Soon" placement="bottom-start">
        <MFlex
          position="relative"
          alignItems="center"
          w="full"
          justifyContent="space-between"
        >
          <MText
            color="inherit"
            truncate
            lineClamp={1}
            fontSize={isSubtitle ? '12px' : 'sm'}
            mb="-2px"
            mt="0px"
            fontWeight={isSelected || isHighlight ? 'bold' : 'light'}
          >
            {title}
          </MText>
          <MIcon as={MdOutlineConstruction} color="inherit" />
        </MFlex>
      </MTooltip>
    );
  }
  return (
    <MText
      color="inherit"
      truncate
      lineClamp={1}
      fontSize={isSubtitle ? '12px' : 'sm'}
      mb="-2px"
      mt="0px"
      fontWeight={isSelected || isHighlight ? 'bold' : 'light'}
    >
      {title}
    </MText>
  );
};

export const ProductForm: FC = () => {
  const { productId = '' } = useParams();
  const navigate = useNavigate();
  const {
    enableTokenCredits,
    showProductPpuOption,
    enableRevRecV2,
    revenueRecognition,
    enableOnetimeDiscountProduct,
  } = useFlags();
  const { isReadOnly, inputVariant } = useSettingsReadOnlyRule();

  const [searchParams, _] = useSearchParams();
  // Copy offering
  const copyProductId = searchParams.get('copyProductId');
  const [loadingCopyProduct, setLoadingCopyProduct] = useState(false);

  const productTypeSelectItems = useMemo(() => {
    const excludedTypes: ProductTypeEnum[] = [];

    if (!enableTokenCredits) {
      excludedTypes.push(
        ProductTypeEnum.ONETIME_TOKEN,
        ProductTypeEnum.RECURRING_TOKEN,
      );
    }

    if (!enableOnetimeDiscountProduct) {
      excludedTypes.push(ProductTypeEnum.DISCOUNT);
    }

    return PRODUCTS.ProductTypeSelectOptions.filter(
      (type: Record<'value' | 'title', string>) =>
        !excludedTypes.includes(type.value as ProductTypeEnum),
    );
  }, [enableOnetimeDiscountProduct, enableTokenCredits]);

  const {
    isLoading: isProductLoading,
    data: product,
    isError,
  } = useGetById<IProductResSchema>('productCatalogProducts', productId!, {
    enabled: !!productId,
    refetchOnWindowFocus: false,
    meta: { showErrorToast: true },
  });

  // The values the form was loaded with, used to patch only what the user changed.
  const loadedFormValues = useMemo(
    () => (product ? getProductFormValues(product) : null),
    [product],
  );

  useEffect(() => {
    if (loadedFormValues) {
      resetFormValues(loadedFormValues);
    } else if (isError) {
      navigate(ROUTES.PRODUCT_LIST);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedFormValues, isError]);

  const { data: recRevSettings, isLoading: isLoadingRevRecSettings } =
    useRevRecSettings({ enabled: revenueRecognition });

  const isProductLocked = !!product?.locked;

  const { mutate: doCreateProduct, isPending: isCreateProductLoading } =
    useCreateEntity('productCatalogProducts', {
      onSuccess: () => navigate(PRODUCT_LIST),
      meta: { showErrorToast: true },
    });

  const { mutate: doUpdateProduct, isPending: isUpdateProductLoading } =
    useUpdateEntity<IProductResSchema, Partial<ProductFormValues>>(
      'productCatalogProducts',
      {
        apiMethodOverride: apiPatch,
        onSuccess: () => navigate(PRODUCT_LIST),
        meta: { showErrorToast: true },
      },
    );
  const [query, setQuery] = useState<string>('');
  const debouncedQuery = useDebounce(query);

  const { data: usageTypes, isLoading: usageTypesLoading } = useGetListData<
    IUsageType,
    any[]
  >(
    'productCatalogUsageTypes',
    {
      config: { ...DEFAULT_PAGER, sortField: 'name', sortOrder: 1, rows: 200 },
      filters: {
        status: { ne: UsageTypeStatusEnum.INACTIVE },
        name: { contains: debouncedQuery },
      },
    },
    {
      select: (data) => {
        return data.content;
      },
      meta: { showErrorToast: true },
    },
  );

  const onChangeQuery = (q: string): void => {
    if (q !== query) {
      setQuery(q);
    }
  };

  const [isRelatedOfferingsExpanded, setRelatedOfferingsExpanded] =
    useState(false);

  const { data: relatedOfferings = [], isLoading: isRelatedOfferingsLoading } =
    useGetListData<IOfferingSummaryResSchema, IOfferingSummaryResSchema[]>(
      'productCatalogOfferings',
      {
        config: { rows: 100, sortField: 'name', sortOrder: 1 },
        filters: productId ? { productId } : {},
      },
      {
        enabled: !!productId && isRelatedOfferingsExpanded,
        select: (res) => res?.content ?? [],
        meta: { showErrorToast: true },
      },
    );

  const isDataLoading =
    (productId && isProductLoading) ||
    isLoadingRevRecSettings ||
    loadingCopyProduct;

  const isSaveLoading =
    isDataLoading || isCreateProductLoading || isUpdateProductLoading;

  const {
    handleSubmit,
    setValue,
    getValues,
    control,
    reset,
    watch,
    setError,
    formState: { errors, isDirty, isValid },
  } = useForm<ProductFormValues>({
    resolver: zodResolver(ProductFormSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      status: ProductStatusEnum.ACTIVE,
      productType: ProductTypeEnum.ADVANCE,
      recognitionMethod: null,
      customFields: product?.customFields || {},
      taxExempt: false,
      customId: '',
      description: '',
      financeId: '',
      name: '',
      sku: '',
      taxItemCode: '',
      oneTimePrepaidCreditConfig: null,
      displayUnitPriceFrequency: null,
      ...product,
      usageTypeIds: product?.usageTypes
        ? product?.usageTypes.map((usageType) => usageType.id)
        : null,
      bucketId: null,
      prorationPolicy: ProrationPolicyEnum.PRORATED,
    },
  });

  const selectedProductType = watch('productType');
  const watchedProrationPolicy = watch('prorationPolicy');

  // Kept separate from the field's value so clearing the number input while
  // editing doesn't flip the toggle off (a falsy/NaN value would otherwise
  // read as "disabled").
  const [isDisplayFrequencyEnabled, setIsDisplayFrequencyEnabled] =
    useState(false);

  // Every `reset()` call must go through here so the toggle stays in sync
  // with whatever `displayUnitPriceFrequency` value was just loaded in.
  const resetFormValues = useCallback(
    (values: Parameters<typeof reset>[0]) => {
      reset(values);
      setIsDisplayFrequencyEnabled(
        !!(values as ProductFormValues | undefined)?.displayUnitPriceFrequency,
      );
    },
    [reset],
  );

  const isProrationOptionVisible =
    selectedProductType === ProductTypeEnum.ADVANCE;
  const isDisplayFrequencyOptionVisible =
    DISPLAY_FREQUENCY_ALLOWED_PRODUCT_TYPE.has(selectedProductType);

  const {
    productType,
    recognitionMethod,
    recognitionDuration,
    usageTypeIds,
    oneTimePrepaidCreditConfig,
  } = getValues();

  const {
    open: isCreateModalOpen,
    onOpen: onOpenCreateModal,
    onClose: onCloseCreateModal,
  } = useDisclosure();

  const {
    open: isBucketModalOpen,
    onOpen: onOpenBucketModal,
    onClose: onCloseBucketModal,
  } = useDisclosure();

  const onCloseModal = (id?: string) => {
    onCloseCreateModal();
    id &&
      setValue('usageTypeIds', [id], {
        shouldDirty: true,
        shouldValidate: true,
      });
  };

  const onBucketModalClose = (id?: string) => {
    onCloseBucketModal();
    id &&
      setValue('bucketId', id, {
        shouldDirty: true,
        shouldValidate: true,
      });
  };

  const onChangeProductType = (newProductType: ProductTypeEnum) => {
    // Reset bucket selection when product type changes
    setValue('bucketId', null);

    // Reset usage types for non-usage products
    if (newProductType !== ProductTypeEnum.USAGE) {
      setValue('usageTypeIds', null);
    }

    // Handle proration policy - only applicable for ADVANCE products
    const prorationPolicy =
      newProductType === ProductTypeEnum.ADVANCE
        ? ProrationPolicyEnum.PRORATED
        : null;
    setValue('prorationPolicy', prorationPolicy, {
      shouldDirty: true,
      shouldValidate: true,
    });

    // Handle prepaid credit configuration
    const prepaidCreditConfig =
      newProductType === ProductTypeEnum.ONETIME_PREPAID_CREDIT
        ? { type: PrePaidCreditConfigTypeEnum.USAGE_PRODUCTS }
        : null;
    setValue('oneTimePrepaidCreditConfig', prepaidCreditConfig, {
      shouldDirty: true,
      shouldValidate: true,
    });

    // Reset display frequency for product types that don't support it
    if (!DISPLAY_FREQUENCY_ALLOWED_PRODUCT_TYPE.has(newProductType)) {
      setValue('displayUnitPriceFrequency', null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setIsDisplayFrequencyEnabled(false);
    }
  };

  useCtrlEnterHotkey(() => {
    // Usage and product forms use the same hotkeys. Don't trigger product submit if modal is open.
    if (!isCreateModalOpen && isDirty && isValid) {
      handleSubmit(onSubmit)();
    }
  });

  useEffect(() => {
    (async () => {
      if (!copyProductId) {
        return;
      }

      if (!copyProductId?.startsWith('prod_')) {
        return;
      }

      setLoadingCopyProduct(true);
      try {
        const productToBeCloned = await doGetProductById(copyProductId);
        resetFormValues({
          ...productToBeCloned,
          status: ProductStatusEnum.ACTIVE,
          usageTypeIds: productToBeCloned?.usageTypes
            ? productToBeCloned?.usageTypes.map((usageType) => usageType.id)
            : null,
          bucketId: productToBeCloned?.bucket?.id || null,
          prorationPolicy:
            productToBeCloned.prorationPolicy ?? ProrationPolicyEnum.PRORATED,
        });
        setTimeout(() => {
          setValue('name', `Copy of ${productToBeCloned.name}`, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }, 10);
      } catch (err) {
        handleApiErrorToast(err);
      }
      setLoadingCopyProduct(false);
      // Don't set editable to false for cloned products to allow editing
    })();
  }, [copyProductId, resetFormValues, setValue]);

  const onSubmit = async (data: ProductFormValues) => {
    const { usageTypeIds, productType } = data;
    if (productType === ProductTypeEnum.USAGE && !usageTypeIds) {
      setError('usageTypeIds', { message: "Usage type can't be empty." });
      return;
    }

    if (
      isDisplayFrequencyOptionVisible &&
      isDisplayFrequencyEnabled &&
      !data.displayUnitPriceFrequency
    ) {
      setError('displayUnitPriceFrequency', {
        type: 'manual',
        message: "Default display frequency can't be empty.",
      });
      return;
    }
    if (
      productType !== ProductTypeEnum.USAGE &&
      usageTypeIds &&
      usageTypeIds.length === 0
    ) {
      delete data.usageTypeIds;
    }

    if (productType !== ProductTypeEnum.ADVANCE) {
      data.prorationPolicy = null;
    }

    if (!productId) {
      doCreateProduct(data);
      return;
    }

    if (!loadedFormValues) {
      return;
    }

    const payload = buildProductPatchPayload(loadedFormValues, data);
    // An empty patch is rejected by the API, and there is nothing to save anyway.
    if (Object.keys(payload).length === 0) {
      navigate(PRODUCT_LIST);
      return;
    }
    doUpdateProduct({ id: productId, payload });
  };

  const watchUsageTypeIds = watch('usageTypeIds');
  const {
    internalValue: internalUsageTypeIdsValue,
    isLoading: isLoadingUsageTypeIdsValue,
    placeholder: placeholderUsageTypeIds,
    onInternalValueChange: onUsageTypeIdsChange,
  } = useCustomSelectValues<IUsageType>({
    value: watchUsageTypeIds || [],
    setValue: (values) =>
      setValue('usageTypeIds', values, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    getByIds: async (ids: string[]) =>
      ids.length > 0
        ? apiGetAllList<IUsageType>('/api/usageTypes', {
            filters: { id: { in: ids } },
          })
        : [],
    label: 'Usage types',
  });

  const lockedIcon = (
    <MFlex>
      <MTooltip
        contentProps={{ mt: -1, ml: 10 }}
        label="Some of the information in this product cannot be edited
because this product is currently being used."
        shouldWrapChildren
        placement="bottom-start"
      >
        <span>
          <MIcon
            w={4}
            h={6}
            ml="3"
            as={MdLock}
            fontSize="xl"
            alignSelf="center"
            style={{
              cursor: 'pointer',
            }}
          />
        </span>
      </MTooltip>
    </MFlex>
  );

  const isFormValid =
    productType === ProductTypeEnum.USAGE
      ? isValid && !!usageTypeIds?.length
      : isValid;

  const isReadOnlyProps = {
    isDisabled: isReadOnly || isSaveLoading,
    isReadOnly,
    variant: isReadOnly ? 'readonly' : 'outline',
  } as const;

  const isBucketEligibleProduct =
    (productType === ProductTypeEnum.RECURRING_TOKEN ||
      productType === ProductTypeEnum.ONETIME_TOKEN) &&
    enableTokenCredits;

  const watchRecognitionMethod = watch('recognitionMethod');
  const isRecognitionDurationEnabled =
    (productType === ProductTypeEnum.ONETIME ||
      productType === ProductTypeEnum.ADVANCE) &&
    watchRecognitionMethod !== null;

  return (
    <MPageContainer
      alignItems="stretch"
      data-testid="product-form"
      enableAccountSearch
    >
      <MPageHeader
        hasBackButton
        title={getHeaderTitle({
          isReadOnly,
          id: productId,
          title: 'Product',
        })}
        status={
          product?.status
            ? PRODUCTS.PRODUCT_STATUS_ENUM_DISPLAY[product?.status]
            : ''
        }
        copyUrl
        id={product?.id}
        tag={
          isProductLocked && product?.status === ProductStatusEnum.ACTIVE
            ? lockedIcon
            : ''
        }
        recordAuditInfo={product}
        entityJson={product}
        extraBadges={
          product?.id
            ? [
                <ExternalAssociationsIcons
                  key="ExternalAssociationsIcons"
                  entity={AssociationEntity.PRODUCT}
                  recordId={product?.id || productId}
                />,
              ]
            : undefined
        }
      >
        {!isReadOnly && (
          <MButton
            form="product-form"
            variant="primary"
            isLoading={isSaveLoading}
            isDisabled={isSaveLoading || !isDirty || !isFormValid}
            type="submit"
            onClick={handleSubmit(onSubmit, logFormError)}
          >
            Save
          </MButton>
        )}

        {!isReadOnly && product?.id && (
          <MBox ml={2}>
            <ProductActions product={product} source="DETAIL" />
          </MBox>
        )}
      </MPageHeader>

      {isDataLoading ? (
        <MFlex justify="center" grow={1} height={'100%'}>
          <MPageLoader height="auto" />
        </MFlex>
      ) : (
        <MBox width="100%" pt={4} pb={16} pl={8}>
          <form
            id="product-form"
            onSubmit={handleSubmit(onSubmit, logFormError)}
          >
            <MGrid templateColumns="repeat(12, 1fr)" rowGap={4} columnGap={8}>
              <MGridItem colSpan={12}>
                <MFormField error={errors.name} label="Product Name" isRequired>
                  <Controller
                    name="name"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <MInput
                        {...field}
                        role="textbox"
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        placeholder="Enter Product Name"
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={12}>
                <MFormField error={errors.description} label="Description">
                  <Controller
                    name="description"
                    control={control}
                    defaultValue=""
                    render={({ field: { value, ...field } }) => (
                      <MInput
                        value={value || ''}
                        {...field}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        placeholder="Product Description"
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={6}>
                <MFormField error={errors.status} label="Status" isRequired>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <MCustomSelect
                        placeholder={STATUS_PLACEHOLDER}
                        items={PRODUCTS.ProductStatusSelectOptions}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        {...field}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem
                key={
                  isProrationOptionVisible
                    ? 'with-proration'
                    : 'without-proration'
                }
                colSpan={isProrationOptionVisible ? 3 : 6}
              >
                <MFormField
                  error={errors.productType}
                  label="Product Type"
                  isRequired
                  tooltip={
                    selectedProductType === ProductTypeEnum.DISCOUNT
                      ? 'Use for discount or credit products. The system will apply negative pricing and handle billing and reporting automatically.'
                      : undefined
                  }
                >
                  <MLockedTextOrContent
                    text={PRODUCTS.PRODUCT_TYPE_DISPLAY[productType]}
                    h={8}
                    isLocked={isProductLocked}
                  >
                    <Controller
                      name="productType"
                      control={control}
                      render={({ field: { onChange, ...rest } }) => (
                        <MCustomSelect
                          placeholder="Select a type"
                          items={productTypeSelectItems}
                          onChange={(event) => {
                            onChange(event);
                            onChangeProductType(event);
                            setValue('recognitionDuration', null);
                            setValue('recognitionMethod', null);
                          }}
                          isDisabled={isSaveLoading}
                          isReadOnly={isReadOnly}
                          variant={inputVariant}
                          {...rest}
                        />
                      )}
                    />
                  </MLockedTextOrContent>
                </MFormField>
              </MGridItem>
              {isProrationOptionVisible && (
                <MGridItem colSpan={3}>
                  <MFormField
                    label="Enable Proration"
                    tooltip={
                      <>
                        <MText color={'tWhite.300'} mb={2}>
                          When enabled: Mid-period additions or adjustments are
                          billed proportionally based on the time remaining.
                        </MText>
                        <MText color={'tWhite.300'}>
                          When disabled: Mid-period additions or adjustments are
                          billed at the standard recurring rate for the cycle.
                        </MText>
                      </>
                    }
                    error={errors.prorationPolicy}
                  >
                    <Controller
                      control={control}
                      name="prorationPolicy"
                      render={({ field: { onChange, onBlur, ref } }) => (
                        <MSwitch
                          ref={ref}
                          onBlur={onBlur}
                          data-testid="toggle-proration"
                          isChecked={
                            watchedProrationPolicy ===
                            ProrationPolicyEnum.PRORATED
                          }
                          role="switch"
                          aria-checked={
                            watchedProrationPolicy ===
                            ProrationPolicyEnum.PRORATED
                          }
                          onChange={(e) => {
                            const newValue = e.target.checked
                              ? ProrationPolicyEnum.PRORATED
                              : ProrationPolicyEnum.NON_PRORATED;
                            onChange(newValue);
                          }}
                          isDisabled={isProductLocked || isSaveLoading}
                        />
                      )}
                    />
                  </MFormField>
                </MGridItem>
              )}
              {recRevSettings?.enabled && (
                <>
                  <MGridItem colSpan={6}>
                    <MFormField
                      error={errors.recognitionMethod}
                      label="Recognition Method"
                      tooltip="Determines how revenue for this product is recognized (e.g., immediately or over a schedule). This selection is locked once the product is created."
                    >
                      <MLockedTextOrContent
                        text={
                          recognitionMethod
                            ? (PRODUCTS.RECOGNITION_METHOD_DISPLAY[
                                recognitionMethod as keyof typeof PRODUCTS.RECOGNITION_METHOD_DISPLAY
                              ] ?? recognitionMethod)
                            : null
                        }
                        h={8}
                        // If product is locked and field would be disabled based on product type, show as read-only
                        isLocked={
                          isProductLocked &&
                          productType !== ProductTypeEnum.ADVANCE
                        }
                      >
                        <Controller
                          name="recognitionMethod"
                          control={control}
                          render={({ field: { onChange, ...rest } }) => (
                            <MCustomSelect
                              placeholder="Recognition Method"
                              items={PRODUCTS.RecognitionMethodOptions}
                              clearable
                              isDisabled={isSaveLoading}
                              isReadOnly={isReadOnly}
                              onChange={(e) => {
                                let value = e;
                                if (e === '') {
                                  value = null;
                                }

                                onChange(value);
                                setValue('recognitionDuration', null);
                              }}
                              {...rest}
                            />
                          )}
                        />
                      </MLockedTextOrContent>
                    </MFormField>
                  </MGridItem>
                  <MGridItem colSpan={6}>
                    {enableRevRecV2 && isRecognitionDurationEnabled && (
                      <MFormField
                        error={errors.recognitionDuration}
                        label="Recognition Duration (days)"
                        tooltip="Optionally enter the number of days revenue for this product should be recognized over. If blank, the full term will be used."
                      >
                        <MLockedTextOrContent
                          text={recognitionDuration || ''}
                          h={8}
                          isLocked={isProductLocked}
                        >
                          <Controller
                            name="recognitionDuration"
                            control={control}
                            render={({
                              field: { value, onChange, ...rest },
                            }) => (
                              <MInput
                                placeholder="Enter a number"
                                type="number"
                                isDisabled={
                                  isSaveLoading || !isRecognitionDurationEnabled
                                }
                                isReadOnly={isReadOnly}
                                variant={inputVariant}
                                value={value || ''}
                                onChange={(e) => {
                                  const numValue = e.target.value
                                    ? Number(e.target.value)
                                    : null;
                                  onChange(numValue);
                                }}
                                {...rest}
                              />
                            )}
                          />
                        </MLockedTextOrContent>
                      </MFormField>
                    )}
                  </MGridItem>
                </>
              )}
              <MGridItem colSpan={6}>
                <MFormField error={errors.customId} label="Custom ID">
                  <Controller
                    name="customId"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        placeholder={CUSTOM_ID_PLACEHOLDER}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        value={value || ''}
                        {...rest}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={6}>
                <MFormField error={errors.sku} label="SKU">
                  <Controller
                    name="sku"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        placeholder={SKU_PLACEHOLDER}
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        value={value || ''}
                        {...rest}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={6}>
                <MFormField error={errors.financeId} label="Finance ID">
                  <Controller
                    name="financeId"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        placeholder="GL Account in finance system"
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        value={value || ''}
                        {...rest}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
              {showProductPpuOption && (
                <MGridItem colSpan={6}>
                  <MFormField
                    error={errors.ppuDisplayPrecision}
                    label="Display Precision (Price Per Unit)"
                    tooltip="Rounds values to the specified number of decimal places for display (does not affect stored value or calculations). If left blank, the default is 2."
                  >
                    <Controller
                      name="ppuDisplayPrecision"
                      control={control}
                      render={({ field: { value, onChange, ...rest } }) => (
                        <MCustomNumberInput
                          placeholder="Enter a number between 0 and 10"
                          isDisabled={isSaveLoading}
                          isReadOnly={isReadOnly}
                          variant={inputVariant}
                          // valueAsNumber is 0 when input is empty, so we need to check valueAsString to allow user to clear the value and set it to null
                          onChange={(valueAsString, valueAsNumber) =>
                            onChange(
                              valueAsString === '' ? null : valueAsNumber,
                            )
                          }
                          value={value ?? ''}
                          {...rest}
                        />
                      )}
                    />
                  </MFormField>
                </MGridItem>
              )}
              {isBucketEligibleProduct && (
                <MGridItem colSpan={6}>
                  <MFormField error={errors.bucketId} label="Bucket">
                    <Controller
                      name="bucketId"
                      control={control}
                      render={({ field }) => (
                        <BucketSearch
                          {...field}
                          isDisabled={isSaveLoading || isProductLocked}
                          isReadOnly={isReadOnly || isProductLocked}
                          isSaveLoading={isSaveLoading}
                          inputVariant={inputVariant}
                          onOpenBucketModal={onOpenBucketModal}
                        />
                      )}
                    />
                  </MFormField>
                </MGridItem>
              )}
              <MGridItem colSpan={12}>
                <MDivider />
              </MGridItem>
              <MGridItem colSpan={4}>
                <MFormField error={errors.taxItemCode} label="Tax Item Code">
                  <Controller
                    name="taxItemCode"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MInput
                        placeholder="Tax Item Code"
                        isDisabled={isSaveLoading}
                        isReadOnly={isReadOnly}
                        variant={inputVariant}
                        value={value || ''}
                        {...rest}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
              <MGridItem colSpan={isDisplayFrequencyOptionVisible ? 2 : 8}>
                <MFormField error={errors.taxExempt} label="" skipLabel>
                  <Controller
                    name="taxExempt"
                    control={control}
                    render={({ field: { value, ...rest } }) => (
                      <MCheckbox
                        minH={8}
                        isChecked={!!value}
                        {...rest}
                        isDisabled={isReadOnlyProps.isDisabled}
                        isReadOnly={isReadOnlyProps.isReadOnly}
                      >
                        <MFlex alignItems="center" position="relative">
                          Tax Exempt
                          <MTooltip
                            label="If selected, this product will not be sent to the tax provider during tax calculation."
                            placement="bottom-start"
                          >
                            <MFlex alignItems="center">
                              <MIcon as={MdInfo} color="tPurple.base" ml="1" />
                            </MFlex>
                          </MTooltip>
                        </MFlex>
                      </MCheckbox>
                    )}
                  />
                </MFormField>
              </MGridItem>
              {isDisplayFrequencyOptionVisible && (
                <MGridItem colSpan={6}>
                  <MFlex direction="column" gap={2} mt={7}>
                    <MFlex align="center">
                      <MText color="tPurple.dark" fontSize="md">
                        Default Display Frequency
                      </MText>
                      <MTooltip
                        shouldWrapChildren
                        label="Default frequency (e.g. monthly, annually) shown on the Quote PDF. This does not impact the billing schedule and can be overridden on Quotes."
                        placement="bottom-start"
                      >
                        <MIcon as={MdInfo} color="tPurple.base" ml="1" />
                      </MTooltip>
                      <Controller
                        control={control}
                        name="displayUnitPriceFrequency"
                        render={({
                          field: { value, onChange, onBlur, ref },
                        }) => (
                          <MSwitch
                            ref={ref}
                            onBlur={onBlur}
                            size="md"
                            ml={2}
                            data-testid="toggle-default-display-frequency"
                            isChecked={isDisplayFrequencyEnabled}
                            role="switch"
                            aria-checked={isDisplayFrequencyEnabled}
                            onChange={(e) => {
                              const { checked } = e.target;
                              setIsDisplayFrequencyEnabled(checked);
                              onChange(checked ? value || 1 : null);
                            }}
                            isDisabled={isReadOnly || isSaveLoading}
                          />
                        )}
                      />
                    </MFlex>
                    {isDisplayFrequencyEnabled && (
                      <MFlex direction="column" gap={2}>
                        <MFlex align="center" gap={2}>
                          <MText>Per</MText>
                          <MFormField
                            w={14}
                            error={errors.displayUnitPriceFrequency}
                            formErrorMessageProps={{ display: 'none' }}
                          >
                            <Controller
                              control={control}
                              name="displayUnitPriceFrequency"
                              render={({
                                field: { value, onChange, ...rest },
                              }) => (
                                <MCustomNumberInput
                                  onChange={(
                                    valueAsString: string,
                                    valueAsNumber: number,
                                  ) =>
                                    onChange(
                                      valueAsString === ''
                                        ? null
                                        : valueAsNumber,
                                    )
                                  }
                                  value={value || ''}
                                  min={1}
                                  max={60}
                                  styleProps={{
                                    'aria-label':
                                      'Default display frequency, in months',
                                  }}
                                  {...rest}
                                  isDisabled={isReadOnly || isSaveLoading}
                                />
                              )}
                            />
                          </MFormField>
                          <MText>Month(s)</MText>
                        </MFlex>
                        {!!errors.displayUnitPriceFrequency && (
                          <MText
                            data-testid="display-frequency-error"
                            color="tRed.base"
                            fontSize="sm"
                          >
                            {errors.displayUnitPriceFrequency.message}
                          </MText>
                        )}
                        <MBox maxW="300px">
                          <Controller
                            control={control}
                            name="displayUnitPriceFrequency"
                            render={({ field: { value, onChange } }) => (
                              <RadioStyleButtonGroup
                                name="displayUnitPriceFrequencyOptions"
                                containerProps={{ columns: 2 }}
                                options={displayFrequencyOptions}
                                value={`${value || ''}` || undefined}
                                defaultValue={`${value || ''}` || undefined}
                                isDisabled={isReadOnly || isSaveLoading}
                                onChange={(newValue) => onChange(+newValue)}
                              />
                            )}
                          />
                        </MBox>
                      </MFlex>
                    )}
                  </MFlex>
                </MGridItem>
              )}
              {selectedProductType === ProductTypeEnum.USAGE && (
                <>
                  <ExtraConfigurationTitle title="Usage Configuration" />

                  {!isProductLocked ? (
                    <MGridItem colSpan={6}>
                      <MFormField
                        error={errors.usageTypeIds}
                        label="Usage Type"
                        isRequired={
                          selectedProductType === ProductTypeEnum.USAGE
                        }
                      >
                        <Controller
                          name="usageTypeIds"
                          control={control}
                          render={({ field }) => (
                            <MCustomMultiSelect
                              {...field}
                              itemTitle="name"
                              itemValue="id"
                              items={usageTypes}
                              prependItems={[
                                {
                                  item: PRODUCTS.NEW_USAGE_TYPE_OPTION,
                                  isAction: true,
                                  hasDivider: true,
                                  onClick: ({ onClose }) => {
                                    onClose && onClose();
                                    onOpenCreateModal();
                                  },
                                },
                              ]}
                              loading={
                                usageTypesLoading || isLoadingUsageTypeIdsValue
                              }
                              showQueryInput
                              useExternalQuery
                              multiple
                              externalQuery={query}
                              onChangeQuery={onChangeQuery}
                              isDisabled={isSaveLoading}
                              isReadOnly={isReadOnly}
                              variant={inputVariant}
                              returnItem
                              value={internalUsageTypeIdsValue}
                              closeButtonText="Apply"
                              placeholder={placeholderUsageTypeIds}
                              onChange={(val) => {
                                onUsageTypeIdsChange(val as any);
                              }}
                              inputProps={{
                                _placeholder: { color: 'tPurple.dark' },
                                _disabled: {
                                  _placeholder: {
                                    color: 'tGray.darkPurple',
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </MFormField>
                    </MGridItem>
                  ) : (
                    <MGridItem colSpan={9}>
                      <UsageTypeView usageTypes={product?.usageTypes} />
                    </MGridItem>
                  )}
                </>
              )}
              {selectedProductType ===
                ProductTypeEnum.ONETIME_PREPAID_CREDIT && (
                <>
                  <ExtraConfigurationTitle title="Pre-Paid Credit Configuration"></ExtraConfigurationTitle>

                  <MGridItem colSpan={6}>
                    <MFormField
                      error={errors.oneTimePrepaidCreditConfig?.type}
                      label="Credit Application"
                      isRequired={
                        selectedProductType ===
                        ProductTypeEnum.ONETIME_PREPAID_CREDIT
                      }
                    >
                      <MLockedTextOrContent
                        text={
                          PRODUCTS.PRE_PAID_CREDIT_CONFIG_TYPE_DISPLAY[
                            oneTimePrepaidCreditConfig?.type!
                          ]
                        }
                        h={8}
                        isLocked={isProductLocked}
                      >
                        <Controller
                          name="oneTimePrepaidCreditConfig.type"
                          control={control}
                          render={({ field: { onChange, ...rest } }) => (
                            <MCustomSelect
                              items={
                                PRODUCTS.PrePaidCreditConfigTypeSelectOptions
                              }
                              renderItemContent={
                                renderPrePaidCreditScopeItemContent
                              }
                              isDisabled={isSaveLoading}
                              isReadOnly={isReadOnly}
                              disabledItems={[
                                PrePaidCreditConfigTypeEnum.ALL_PRODUCTS,
                                PrePaidCreditConfigTypeEnum.SPECIFIC_PRODUCTS,
                                PrePaidCreditConfigTypeEnum.SUBSCRIPTION_PRODUCTS_ONLY,
                              ]}
                              {...rest}
                              onChange={onChange}
                            />
                          )}
                        />
                      </MLockedTextOrContent>
                    </MFormField>
                  </MGridItem>
                </>
              )}
              <MGridItem colSpan={12}>
                <Controller
                  name="customFields"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <CustomFieldDataForm
                      accordionAutoExpanded
                      entity={CustomFieldEntityEnum.PRODUCT}
                      value={value || {}}
                      setValue={(val) => {
                        onChange(val);
                      }}
                      fieldColSpan={4}
                      mode="accordion"
                      {...isReadOnlyProps}
                      showDivider
                    />
                  )}
                />
              </MGridItem>
              {productId && (
                <MGridItem colSpan={12}>
                  <MDivider mt={4} mb={4} />
                  <RelatedOfferingsSection
                    offerings={relatedOfferings}
                    isLoading={isRelatedOfferingsLoading}
                    onAccordionOpen={() => setRelatedOfferingsExpanded(true)}
                  />
                </MGridItem>
              )}
            </MGrid>
          </form>
        </MBox>
      )}
      {selectedProductType === ProductTypeEnum.USAGE && isCreateModalOpen && (
        <UsageTypeForm isOpen={isCreateModalOpen} onClose={onCloseModal} />
      )}
      {isBucketEligibleProduct && isBucketModalOpen && (
        <BucketForm isOpen={isBucketModalOpen} onClose={onBucketModalClose} />
      )}
    </MPageContainer>
  );
};
